import { useState } from 'react';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { helpers } from '@/client/utils/helpers';
import { useTeamMembers, useCreateTeamMembers, useUpdateTeamMember, useUpdateTeamMembers, useDeleteTeamMember } from '@/lib/sdk/team/queries';
import { exportUsersToCSV, convertToCreateDto, convertToUpdateDto, type CSVParseResult } from '@/utility/csv';
import type { TeamMemberFormData } from '@/client/utils/validationSchemas';
import type { TeamMember } from '@/lib/sdk/team/types';

export function useTeamPage() {
  const { currentOrg, user, userPermission, featurePermissions } = useAuth();
  const toast = useToast();

  const { data, isLoading } = useTeamMembers({ org_id: currentOrg!.id, offset: 0, limit: 1200 });
  const members = data?.items ?? [];

  const { mutate: addMember }    = useCreateTeamMembers();
  const { mutate: updateMember } = useUpdateTeamMember();
  const { mutate: deleteMember } = useDeleteTeamMember();
  const importCreateMutation = useCreateTeamMembers();
  const importUpdateMutation = useUpdateTeamMembers();

  const [isAddDialogOpen, setIsAddDialogOpen]     = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingMember, setEditingMember]         = useState<TeamMember | null>(null);
  const [deletingMember, setDeletingMember]       = useState<TeamMember | null>(null);

  const isSuperAdmin      = user?.is_super_admin === true;
  const isCurrentUserAdmin = isSuperAdmin || userPermission === 'admin';
  const canAddTeam        = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'team', 'add');
  const canEditTeam       = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'team', 'edit');
  const canDeleteTeam     = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'team', 'delete');
  const canManageTeam     = canAddTeam || canEditTeam || canDeleteTeam;
  const canImportTeam     = canAddTeam || canEditTeam;
  const isImporting       = importCreateMutation.isPending || importUpdateMutation.isPending;

  const breadcrumbItems = [
    { label: 'Dashboard', href: `/client/${currentOrg!.id}/reports` },
    { label: 'Team', href: `/client/${currentOrg!.id}/team` },
  ];

  const handleAddMember = (data: TeamMemberFormData) => {
    addMember({ users: [{ name: data.name, country_code: data.country_code, phone_number: data.phone_number, org_code: data.org_code || undefined, nickname: data.nickname || undefined, permissions: data.org_permissions, feature_permissions: data.feature_permissions, adhoc_tasks_enabled: data.adhoc_tasks_enabled, instant_task_scope: data.instant_task_scope, instant_task_members: data.instant_task_members }] }, {
      onSuccess: () => { toast.success('Team member added successfully'); setIsAddDialogOpen(false); },
      onError: (error: any) => toast.error(error.message || 'Failed to add team member'),
    });
  };

  const handleUpdateMember = (data: TeamMemberFormData) => {
    if (!editingMember) return;
    updateMember(
      { id: editingMember.id, data: { users: [{ id: editingMember.id, name: data.name, country_code: data.country_code, phone_number: data.phone_number, org_code: data.org_code || '', nickname: data.nickname || undefined, permissions: data.org_permissions, feature_permissions: data.feature_permissions, adhoc_tasks_enabled: data.adhoc_tasks_enabled, instant_task_scope: data.instant_task_scope, instant_task_members: data.instant_task_members }] } },
      {
        onSuccess: () => { toast.success('Team member updated successfully'); setEditingMember(null); },
        onError: (error: any) => toast.error(error.message || 'Failed to update team member'),
      }
    );
  };

  const handleDeleteMember = () => {
    if (!deletingMember) return;
    deleteMember(deletingMember.id, {
      onSuccess: () => { toast.success('Team member deleted successfully'); setDeletingMember(null); },
      onError: (error: any) => toast.error(error.message || 'Failed to delete team member'),
    });
  };

  const handleExportUsers = () => {
    if (members.length === 0) {
      toast.warning('There are no team members to export');
      return;
    }
    exportUsersToCSV(members, 'team-members.csv');
    toast.success(`${members.length} team members exported`);
  };

  const handleImportUsers = async (parseResult: CSVParseResult) => {
    const createDto = convertToCreateDto(parseResult.newUsers);
    const updateDto = convertToUpdateDto(
      parseResult.existingUsers,
      (id) => members.find((member) => member.id === id)?.org_permissions
    );

    if (createDto.users.length > 0) {
      await importCreateMutation.mutateAsync(createDto);
    }
    if (updateDto.users.length > 0) {
      await importUpdateMutation.mutateAsync(updateDto);
    }

    toast.success(
      `${createDto.users.length + updateDto.users.length} team members imported (${createDto.users.length} created, ${updateDto.users.length} updated)`
    );
  };

  return {
    currentOrg: currentOrg!,
    members, isLoading,
    isAddDialogOpen, setIsAddDialogOpen,
    isImportDialogOpen, setIsImportDialogOpen, isImporting,
    editingMember, setEditingMember,
    deletingMember, setDeletingMember,
    isCurrentUserAdmin,
    canAddTeam, canEditTeam, canDeleteTeam, canManageTeam, canImportTeam,
    breadcrumbItems,
    handleAddMember, handleUpdateMember, handleDeleteMember,
    handleExportUsers, handleImportUsers,
  };
}
